import { useEffect, useRef } from 'react';
import { ArrowLeft, ChevronRight, Pin, PinOff, Target, X } from 'lucide-react';
import { AgentDot } from '../../../AgentDot';
import { Badge, Button, Card, IconButton, ScrollArea, Typography } from '../../ui';
import { TaskDetailModal } from '../../tasks/TaskDetailModal';
import { useTaskSidePanel } from '../../task-side-panel/TaskSidePanelContext';
import { useTranslation } from '../../../../lib/i18n';
import type { WorldGoalView } from '../../../../lib/home-map/world-goals';
import type { WorldAgent } from '../../../../lib/home-map/living-world';
import styles from './LivingWorld.module.css';

export function WorldGoalMenu({
  goals,
  selectedId,
  agents,
  onSelect,
  onTask,
  onClose,
  onAction,
  pinnedIds,
  editable,
  onPin,
  onClear,
  onBack,
}: {
  goals: WorldGoalView[];
  selectedId: string | null;
  agents: WorldAgent[];
  onSelect: (id: string) => void;
  onTask: (id: string) => boolean;
  onClose: () => void;
  onAction: (name: string, action: () => void) => void;
  pinnedIds: string[];
  editable: boolean;
  onPin: (id: string) => void;
  onClear: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { openModal } = useTaskSidePanel();
  const panel = useRef<HTMLElement>(null);
  const goal = goals.find((goal) => goal.id === selectedId);
  useEffect(() => {
    panel.current?.focus({ preventScroll: true });
  }, [selectedId]);
  return (
    <Card
      ref={panel}
      component="section"
      variant="shadow"
      size="sm"
      disablePadding
      className={styles.agentMenu}
      tabIndex={-1}
      aria-label={goal?.title || t('home.world.missions')}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onAction('goal-close', onClose);
        }
      }}
    >
      <div className={styles.menuHeader}>
        {goal ? (
          <IconButton size="small" aria-label={t('common.back')} onClick={() => onAction('mission-back', onBack)}>
            <ArrowLeft size={16} aria-hidden />
          </IconButton>
        ) : (
          <Target size={24} aria-hidden />
        )}
        <div className={styles.identity}>
          <Typography variant="h4">{goal?.title || t('home.world.missions')}</Typography>
          <Typography variant="p3" color="secondary">
            {t('home.world.missions')} · {pinnedIds.length}/3
          </Typography>
        </div>
        <IconButton size="small" aria-label={t('common.close')} onClick={() => onAction('goal-close', onClose)}>
          <X size={16} aria-hidden />
        </IconButton>
      </div>
      <ScrollArea className={styles.menuBody}>
        {goal ? (
          <>
            <div className={styles.menuDetails}>
              <Button
                size="small"
                variant={goal.pinned ? 'contained' : 'outlined'}
                startIcon={goal.pinned ? <PinOff size={14} aria-hidden /> : <Pin size={14} aria-hidden />}
                disabled={!editable || (!goal.pinned && pinnedIds.length >= 3)}
                onClick={() => onAction('mission-pin', () => onPin(goal.id))}
              >
                {t(goal.pinned ? 'home.world.unpinGoal' : 'home.world.pinGoal')}
              </Button>
              <Typography variant="h4">
                {t(goal.partial ? 'home.world.goalProgressPartial' : 'home.world.goalProgress', { done: goal.completed, total: goal.total })}
              </Typography>
              {goal.brief && <Typography variant="p2">{goal.brief}</Typography>}
              {goal.partial && (
                <Typography variant="p3" color="secondary">
                  {t('home.world.goalHistory')}
                </Typography>
              )}
            </div>
            {goal.tasks.map((task) => (
              <Button
                key={task.id}
                fullWidth
                variant="text"
                className={styles.attentionRow}
                startIcon={task.agentId ? <AgentDot id={task.agentId} size={28} /> : undefined}
                endIcon={<ChevronRight size={16} aria-hidden />}
                onClick={() =>
                  onAction('goal-task', () => {
                    if (!onTask(task.id)) openModal(<TaskDetailModal taskId={task.id} />, task.id);
                  })
                }
              >
                <span className={styles.attentionIdentity}>{task.title}</span>
                <span className={styles.attentionRole}>
                  {agents.find((agent) => agent.id === task.agentId)?.name || t('home.world.agentUnknown')}
                </span>
                <Badge
                  size="small"
                  label={t(task.statusKey)}
                  variant={
                    task.state === 'needsYou'
                      ? 'waiting-on-you'
                      : task.state === 'failed'
                      ? 'failed'
                      : task.statusKey === 'tasks.status.done'
                      ? 'done'
                      : ['tasks.status.activeTask', 'tasks.status.working', 'tasks.status.inReview'].includes(task.statusKey)
                      ? 'in-progress'
                      : 'waiting-on-a-reply'
                  }
                />
              </Button>
            ))}
          </>
        ) : (
          <>
            <Typography variant="p3" color="secondary">
              {t('home.world.missionsHint')}
            </Typography>
            {editable && pinnedIds.some((id) => !goals.some((goal) => goal.id === id)) && (
              <Button size="small" variant="text" onClick={() => onAction('mission-clear', onClear)}>
                {t('apps.share.lend.clear')}
              </Button>
            )}
            {goals.length ? (
              [...goals]
                .sort((a, b) => Number(b.pinned) - Number(a.pinned))
                .map((goal) => (
                  <div key={goal.id} className={styles.missionRow}>
                    <Button
                      fullWidth
                      variant="text"
                      className={styles.attentionRow}
                      endIcon={<ChevronRight size={16} aria-hidden />}
                      onClick={() => onAction('goal-select', () => onSelect(goal.id))}
                    >
                      <span className={styles.attentionIdentity}>{goal.title}</span>
                      <span className={styles.attentionRole}>
                        {t(goal.partial ? 'home.world.goalProgressPartial' : 'home.world.goalProgress', { done: goal.completed, total: goal.total })}
                      </span>
                      {goal.tasks.some((task) => task.state === 'needsYou') && (
                        <Badge
                          size="small"
                          variant="waiting-on-you"
                          label={`${t('tasks.status.actionNeeded')} · ${goal.tasks.filter((task) => task.state === 'needsYou').length}`}
                        />
                      )}
                      {goal.tasks.some((task) => task.state === 'failed') && (
                        <Badge
                          size="small"
                          variant="failed"
                          label={`${t('tasks.status.failed')} · ${goal.tasks.filter((task) => task.state === 'failed').length}`}
                        />
                      )}
                      {goal.tasks.some((task) => task.statusKey === 'tasks.status.blocked') && (
                        <Badge
                          size="small"
                          variant="waiting-on-a-reply"
                          label={`${t('tasks.status.blocked')} · ${goal.tasks.filter((task) => task.statusKey === 'tasks.status.blocked').length}`}
                        />
                      )}
                    </Button>
                    <IconButton
                      size="small"
                      aria-label={`${t(goal.pinned ? 'home.world.unpinGoal' : 'home.world.pinGoal')} · ${goal.title}`}
                      disabled={!editable || (!goal.pinned && pinnedIds.length >= 3)}
                      onClick={() => onAction('mission-pin', () => onPin(goal.id))}
                    >
                      {goal.pinned ? <PinOff size={16} aria-hidden /> : <Pin size={16} aria-hidden />}
                    </IconButton>
                  </div>
                ))
            ) : (
              <Typography variant="p2">{t('goals.empty.title')}</Typography>
            )}
          </>
        )}
      </ScrollArea>
      {goal && (
        <div className={styles.menuFooter}>
          <Button size="small" variant="outlined" href={`/goals?goal=${encodeURIComponent(goal.id)}`} onClick={() => onAction('goal-open', () => {})}>
            {t('tasks.decision.openGoal')}
          </Button>
        </div>
      )}
    </Card>
  );
}
